"use client";
import { useEffect, useState } from "react";
import { isWithinRTH } from "@/lib/marketHours";
import { scannerStatusReader } from "@/lib/scanner/statusPoll";
import { useMarketSession } from "@/lib/useMarketOpen";

/**
 * Market-session badge pinned to the top nav — ONE pill whose label + color
 * change with the current US-equity session:
 *
 *   Pre-Market   (amber)              · weekday 04:00–09:30 ET
 *   Live         (emerald, pulsing)   · weekday 09:30–16:00 ET
 *   Post-Market  (violet)             · weekday 16:00–20:00 ET
 *   Closed       (slate)              · overnight / weekends / holidays
 *
 * The session is driven by `useMarketSession` (a client-clock,
 * asset-class-aware session), so pre-market and post-market are distinct —
 * the server's coarse `marketState` only knew rth / after-hours / closed.
 *
 * `/api/scanner/status` is polled every 30 s during RTH while the tab is
 * visible, so the Live badge can turn red ("Worker down" / "Stale") when the
 * breadth-worker is down or RTH data has gone stale — a wedged worker can't
 * masquerade as a healthy green "Live". `last_refresh` age alone is not
 * enough: a mid-cycle scanner is busy, not stale, so the pill also wants
 * a fresh scanner alive/heartbeat ping. Closed / hidden = one mount fetch.
 * The read is the tab's one reader (lib/scanner/statusPoll.ts): this pill is
 * mounted twice, and until 2026-10-20 each mount fetched on its own.
 */

type MarketState = "rth" | "after-hours" | "closed";

type Status = {
  lastRefreshMs: number | null;
  isWithinRth: boolean;
  marketState?: MarketState;
  universeActive: number;
  scannerWorkerHeartbeatMs?: number | null;
  scannerWorkerAliveMs?: number | null;
  breadthWorkerHeartbeatMs?: number | null;
  breadthWorkerAliveMs?: number | null;
};

const RTH_CYCLE_MS = 5 * 60 * 1000;
// A breadth tick takes ~100-130s on the (slower) VPS, so back-to-back ticks can
// legitimately gap past 3 min under load and flash a false "Worker down". 6 min
// still trips on a genuinely wedged worker (its heartbeat goes stale forever)
// but never on normal slow ticks.
const WORKER_DOWN_MS = 6 * 60 * 1000;

type SessionKey = "pre-market" | "live" | "post-market" | "closed";
const CFG: Record<
  SessionKey,
  { label: string; dot: string; wrap: string; pulse: boolean }
> = {
  "pre-market": {
    label: "Pre-Market",
    dot: "bg-warn shadow-[0_0_0_4px_rgb(var(--mpp-warn)/0.20)]",
    wrap: "border-warn/40 bg-warn-soft text-warn",
    pulse: false,
  },
  live: {
    label: "Live",
    dot: "bg-bull shadow-[0_0_0_4px_rgb(var(--mpp-bull)/0.25)]",
    wrap: "border-bull/40 bg-bull-soft text-bull-ink",
    pulse: true,
  },
  "post-market": {
    label: "Post-Market",
    dot: "bg-accent shadow-[0_0_0_4px_rgb(var(--mpp-accent)/0.20)]",
    wrap: "border-accent/40 bg-accent-soft text-accent-ink",
    pulse: false,
  },
  closed: {
    label: "Closed",
    dot: "bg-ink-faint",
    wrap: "border-line bg-surface-muted text-ink-muted",
    pulse: false,
  },
};

export function DataVersionPill({
  className = "",
  compact = false,
}: {
  className?: string;
  /** Smaller pill for tight chrome (chart legend status line). */
  compact?: boolean;
} = {}) {
  const session = useMarketSession("us-equity") as SessionKey;
  const [status, setStatus] = useState<Status | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const j = await scannerStatusReader<Status>().read();
        if (!cancelled) setStatus(j);
      } catch {
        /* ignore */
      }
    };
    load();
    // Re-render once a second so the "updated Ns ago" age advances. Gated on
    // visibility like the fetch tick below, which had the guard while this one
    // did not — and this component is mounted TWICE in TopNav, so it was two
    // state updates per second on every page, forever, including on a tab
    // nobody is looking at. Nothing to show means nothing to re-render.
    const tickRender = setInterval(() => {
      if (document.hidden) return;
      setStatus((s) => (s ? { ...s } : s));
    }, 1000);
    const tickFetch = setInterval(() => {
      if (document.hidden || !isWithinRTH()) return;
      void load();
    }, 30_000);
    const onVis = () => {
      if (!document.hidden && isWithinRTH()) void load();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(tickRender);
      clearInterval(tickFetch);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Data-health overlay (RTH only) — a down worker / stale data turns the
  // Live badge red so a wedge can't read as a healthy green "Live".
  const ms: MarketState =
    status?.marketState ?? (status?.isWithinRth ? "rth" : "closed");
  const ageMs = status?.lastRefreshMs != null ? Date.now() - status.lastRefreshMs : null;
  const scannerBeat = Math.max(
    status?.scannerWorkerHeartbeatMs ?? 0,
    status?.scannerWorkerAliveMs ?? 0,
  );
  const scannerLive =
    scannerBeat > 0 && Date.now() - scannerBeat < WORKER_DOWN_MS;
  const breadthBeat = Math.max(
    status?.breadthWorkerHeartbeatMs ?? 0,
    status?.breadthWorkerAliveMs ?? 0,
  );
  // last_refresh only bumps when a cycle FINISHES. A mid-cycle worker
  // (history fill, full panel) is busy, not stale — the alive ping is
  // the honest signal. Stale = the data is old AND nobody is working.
  const stale =
    ms === "rth" &&
    ageMs != null &&
    ageMs > RTH_CYCLE_MS * 2 &&
    !scannerLive;
  const workerDown =
    ms === "rth" &&
    status != null &&
    (status.breadthWorkerHeartbeatMs !== undefined ||
      status.breadthWorkerAliveMs !== undefined) &&
    (breadthBeat === 0 || Date.now() - breadthBeat > WORKER_DOWN_MS);
  const liveProblem = session === "live" && (workerDown || stale);

  const c = CFG[session] ?? CFG.closed;
  const label = liveProblem ? (workerDown ? "Worker down" : "Stale") : c.label;
  const dotCls = liveProblem ? "bg-bear" : c.dot;
  const wrapCls = liveProblem
    ? "border-bear/40 bg-bear-soft text-bear-ink"
    : c.wrap;
  const pulse = c.pulse && !liveProblem;

  const freshness =
    status?.lastRefreshMs == null
      ? ""
      : session === "live"
        ? ` · live ${fmtAge(ageMs ?? 0)}`
        : ` · as of ${fmtClock(status.lastRefreshMs)}`;
  const title =
    `Market: ${label}${freshness}` +
    (status ? ` · universe ${status.universeActive.toLocaleString()} symbols` : "");

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border font-semibold tracking-wide ${
        compact ? "px-1.5 py-0.5 text-2xs" : "px-2.5 py-1 text-xs"
      } ${wrapCls} ${className}`}
      title={title}
    >
      <span
        className={`relative inline-flex rounded-full ${
          compact ? "h-1.5 w-1.5" : "h-2.5 w-2.5"
        } ${dotCls}`}
      >
        {pulse && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-bull opacity-60" />
        )}
      </span>
      {label}
    </span>
  );
}

function fmtAge(ms: number): string {
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 60 * 60_000) return `${Math.floor(ms / 60_000)}m ago`;
  return `${Math.floor(ms / (60 * 60_000))}h ago`;
}

function fmtClock(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleString("en-US", {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
